import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  BookOpen,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Table,
  Users,
  Package,
  UsersIcon,
  Building,
  Upload,
  Download,
  Edit,
  Trash2,
  CheckCircle,
  Settings
} from 'lucide-react';
import '../styles/Manual.css';

const Manual = () => {
  const { isSuperAdmin } = useSelector(state => state.auth);
  const [activeSection, setActiveSection] = useState('maestro-supply');
  const [openSections, setOpenSections] = useState([]);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const mainContentRef = useRef(null);

  // Scroll al inicio cuando cambia la sección activa
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }

    // También intentar hacer scroll en el layout-content
    const layoutContent = document.querySelector('.layout-content');
    if (layoutContent) {
      layoutContent.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const toggleSection = (sectionId) => {
    const isCurrentlyOpen = openSections.includes(sectionId);

    if (isCurrentlyOpen) {
      setOpenSections(openSections.filter(id => id !== sectionId));
    } else {
      setOpenSections([...openSections, sectionId]);
      setActiveSection(sectionId);

      // Hacer scroll al contenido después de que se abra
      setTimeout(() => {
        const element = document.getElementById(`section-content-${sectionId}`);
        if (element) {
          // Scroll en el layout-content
          const layoutContent = document.querySelector('.layout-content');
          if (layoutContent) {
            const elementTop = element.getBoundingClientRect().top;
            const layoutTop = layoutContent.getBoundingClientRect().top;
            const scrollPosition = element.offsetTop - layoutTop - 100; // 100px de offset

            layoutContent.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          } else {
            // Fallback a scrollIntoView
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 350); // Esperar a que termine la animación
    }
  };

  const allSections = [
    {
      id: 'maestro-supply',
      title: 'Maestro Supply',
      icon: Table,
      description: 'Gestión de la matriz de órdenes de compra y venta'
    },
    {
      id: 'clientes',
      title: 'Gestión de Clientes',
      icon: Users,
      description: 'Administración de clientes y contactos'
    },
    {
      id: 'proveedores',
      title: 'Gestión de Proveedores',
      icon: Package,
      description: 'Administración de proveedores y productos'
    },
    {
      id: 'equipo',
      title: 'Gestión de Equipo',
      icon: UsersIcon,
      description: 'Administración de usuarios y roles'
    },
    {
      id: 'configuracion',
      title: 'Configuración de Empresa',
      icon: Settings,
      description: 'Personaliza información y apariencia de tu empresa'
    },
    {
      id: 'empresas',
      title: 'Panel Super Admin',
      icon: Building,
      description: 'Gestión multiempresa (Solo Super Admin)',
      superAdminOnly: true
    }
  ];

  // Filtrar secciones según el rol del usuario
  const sections = allSections.filter(section => {
    if (section.superAdminOnly) {
      return isSuperAdmin;
    }
    return true;
  });

  const renderContent = (sectionId) => {
    if (sectionId === 'maestro-supply') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Maestro Supply - Matriz de Órdenes</h2>
            <p>
              El Maestro Supply es el corazón del sistema. Es una matriz Excel interactiva donde se
              registran todas las órdenes de compra y venta, permitiendo tener una visión completa del negocio.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Upload size={24} style={{color: themeColor}} />
                  <h3>Preparar el archivo Excel</h3>
                </div>
                <p className="paso-description">
                  Asegúrate de que tu archivo Excel tenga las siguientes columnas principales:
                </p>
                <div className="paso-actions">
                  <h4>Columnas requeridas:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> ITEM - Número de ítem</li>
                    <li><CheckCircle size={16} /> CLIENTE - Nombre del cliente</li>
                    <li><CheckCircle size={16} /> DESCRIPCION - Descripción del producto</li>
                    <li><CheckCircle size={16} /> CANTIDAD - Cantidad de productos</li>
                    <li><CheckCircle size={16} /> P.U. $CLP - Precio unitario al cliente</li>
                    <li><CheckCircle size={16} /> PROVEEDOR - Nombre del proveedor</li>
                    <li><CheckCircle size={16} /> PU PROVEEDOR - Precio unitario del proveedor</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Upload size={24} style={{color: themeColor}} />
                  <h3>Cargar el archivo</h3>
                </div>
                <p className="paso-description">
                  Haz clic en el botón "Importar Excel" en la sección Maestro y selecciona tu archivo.
                  El sistema mostrará una vista previa de los datos a importar.
                </p>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Edit size={24} style={{color: themeColor}} />
                  <h3>Gestionar ítems duplicados</h3>
                </div>
                <p className="paso-description">
                  Si el sistema detecta ítems que ya existen en la matriz, aparecerá un modal con opciones
                  para manejar estos duplicados:
                </p>
                <div className="paso-actions">
                  <h4>Opciones disponibles:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> 🔄 <strong>Sobrescribir</strong> - Actualizar los registros existentes con los nuevos datos del Excel</li>
                    <li><CheckCircle size={16} /> ⏭️ <strong>Omitir</strong> - Mantener los registros existentes sin cambios, ignorando los nuevos datos</li>
                    <li><CheckCircle size={16} /> ❓ <strong>Preguntar por cada uno</strong> - Decidir caso por caso durante la importación</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Vista de comparación:</h4>
                  <p className="paso-description">
                    Al seleccionar "Preguntar por cada uno", verás una tabla comparativa mostrando
                    <strong> Valores actuales</strong> vs <strong>Nuevos valores</strong> para cada campo,
                    permitiéndote tomar la mejor decisión para cada ítem duplicado.
                  </p>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Mapear clientes y proveedores</h3>
                </div>
                <p className="paso-description">
                  Si el sistema detecta clientes o proveedores nuevos, te pedirá que decidas:
                </p>
                <div className="paso-actions">
                  <h4>Opciones disponibles:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Crear un nuevo registro en el sistema</li>
                    <li><CheckCircle size={16} /> Mapear a un cliente/proveedor existente</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">5</div>
              <div className="paso-content">
                <div className="paso-header">
                  <CheckCircle size={24} style={{color: themeColor}} />
                  <h3>Confirmar importación</h3>
                </div>
                <p className="paso-description">
                  Revisa el resumen de la importación y confirma. Los datos se cargarán en la matriz
                  y estarán disponibles inmediatamente para su edición.
                </p>
                <div className="paso-actions">
                  <h4>Funcionalidades disponibles:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Editar celdas con doble clic</li>
                    <li><CheckCircle size={16} /> Filtrar por columnas</li>
                    <li><CheckCircle size={16} /> Exportar a Excel</li>
                    <li><CheckCircle size={16} /> Eliminar filas seleccionadas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'clientes') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestión de Clientes</h2>
            <p>
              Administra tu base de datos de clientes y sus contactos clave.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Crear un cliente nuevo</h3>
                </div>
                <p className="paso-description">
                  Accede a la sección "Clientes" y haz clic en "Nuevo Cliente"
                </p>
                <div className="paso-actions">
                  <h4>Información requerida:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Razón Social (nombre legal)</li>
                    <li><CheckCircle size={16} /> Nombre Comercial (nombre de fantasía)</li>
                    <li><CheckCircle size={16} /> RUT o identificador fiscal</li>
                    <li><CheckCircle size={16} /> Industria (ej: Minería)</li>
                    <li><CheckCircle size={16} /> Dirección y datos de contacto</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Gestión de Contactos</h3>
                </div>
                <p className="paso-description">
                  Haz clic en "Editar Cliente" para acceder a la gestión de contactos. Puedes agregar múltiples
                  contactos para cada cliente con su información específica.
                </p>
                <div className="paso-actions">
                  <h4>Datos del contacto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre completo</li>
                    <li><CheckCircle size={16} /> Cargo en la empresa</li>
                    <li><CheckCircle size={16} /> Email y teléfono</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Funcionalidades:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Editar información del cliente</li>
                    <li><CheckCircle size={16} /> Agregar y gestionar contactos</li>
                    <li><CheckCircle size={16} /> Ver historial de órdenes</li>
                    <li><CheckCircle size={16} /> Eliminar cliente (soft delete)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'proveedores') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestión de Proveedores</h2>
            <p>
              Administra tu red de proveedores, sus productos/servicios, contactos y condiciones comerciales.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Package size={24} style={{color: themeColor}} />
                  <h3>Crear un proveedor nuevo</h3>
                </div>
                <p className="paso-description">
                  Ve a "Proveedores" y haz clic en "Nuevo Proveedor"
                </p>
                <div className="paso-actions">
                  <h4>Información básica:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Razón Social y Nombre Comercial</li>
                    <li><CheckCircle size={16} /> RUT o identificador fiscal</li>
                    <li><CheckCircle size={16} /> Industria</li>
                    <li><CheckCircle size={16} /> Términos de pago (ej: 30 días)</li>
                    <li><CheckCircle size={16} /> Dirección y datos de contacto</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Gestión de Contactos</h3>
                </div>
                <p className="paso-description">
                  Haz clic en "Editar Proveedor" para acceder a la gestión de contactos. Registra los contactos
                  clave: vendedores, administración, servicio técnico.
                </p>
                <div className="paso-actions">
                  <h4>Datos del contacto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre completo</li>
                    <li><CheckCircle size={16} /> Cargo en la empresa</li>
                    <li><CheckCircle size={16} /> Email y teléfono</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Package size={24} style={{color: themeColor}} />
                  <h3>Productos/Servicios</h3>
                </div>
                <p className="paso-description">
                  En la vista de edición del proveedor, accede a "Productos/Servicios" para agregar el catálogo
                  de productos que ofrece este proveedor.
                </p>
                <div className="paso-actions">
                  <h4>Información del producto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Código del producto (ej: PRD-001)</li>
                    <li><CheckCircle size={16} /> Nombre/Descripción (ej: Martillo)</li>
                    <li><CheckCircle size={16} /> Unidad de medida (ej: UN, KG, MT)</li>
                    <li><CheckCircle size={16} /> Tiempo de entrega (ej: 15 días)</li>
                    <li><CheckCircle size={16} /> Precio Neto (ej: $5.000)</li>
                    <li><CheckCircle size={16} /> IVA (calculado automáticamente: $950)</li>
                    <li><CheckCircle size={16} /> Total (calculado automáticamente: $5.950)</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Funcionalidades:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Editar información del proveedor</li>
                    <li><CheckCircle size={16} /> Agregar y gestionar contactos</li>
                    <li><CheckCircle size={16} /> Administrar catálogo de productos/servicios</li>
                    <li><CheckCircle size={16} /> Ver historial de órdenes de compra</li>
                    <li><CheckCircle size={16} /> Eliminar proveedor (soft delete)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'equipo') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestión de Equipo</h2>
            <p>
              Administra los usuarios que tienen acceso al sistema, sus roles y permisos.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <UsersIcon size={24} style={{color: themeColor}} />
                  <h3>Super Admin</h3>
                </div>
                <p className="paso-description">
                  Acceso total al sistema, puede gestionar múltiples empresas
                </p>
                <div className="paso-actions">
                  <h4>Permisos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Crear y gestionar empresas</li>
                    <li><CheckCircle size={16} /> Asignar usuarios a empresas</li>
                    <li><CheckCircle size={16} /> Configuración global</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <UsersIcon size={24} style={{color: themeColor}} />
                  <h3>Administrador de Empresa</h3>
                </div>
                <p className="paso-description">
                  Gestión completa de su empresa específica
                </p>
                <div className="paso-actions">
                  <h4>Permisos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Gestionar Maestro Supply</li>
                    <li><CheckCircle size={16} /> Administrar clientes y proveedores</li>
                    <li><CheckCircle size={16} /> Invitar usuarios al equipo</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <UsersIcon size={24} style={{color: themeColor}} />
                  <h3>Usuario Estándar</h3>
                </div>
                <p className="paso-description">
                  Acceso de lectura y edición limitada
                </p>
                <div className="paso-actions">
                  <h4>Permisos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Ver y editar Maestro Supply</li>
                    <li><CheckCircle size={16} /> Ver clientes y proveedores</li>
                    <li><CheckCircle size={16} /> No puede eliminar datos</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'configuracion') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Configuración de Empresa</h2>
            <p>
              Personaliza la información y apariencia de tu empresa. Solo los Administradores
              tienen acceso a esta sección.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Settings size={24} style={{color: themeColor}} />
                  <h3>Logo y Marca</h3>
                </div>
                <p className="paso-description">
                  Personaliza la identidad visual de tu empresa en el sistema
                </p>
                <div className="paso-actions">
                  <h4>Configuraciones disponibles:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Logo de la empresa (imagen)</li>
                    <li><CheckCircle size={16} /> Nombre Comercial</li>
                    <li><CheckCircle size={16} /> Descripción de la empresa</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Información Legal</h3>
                </div>
                <p className="paso-description">
                  Configura los datos legales y fiscales de tu empresa
                </p>
                <div className="paso-actions">
                  <h4>Datos requeridos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Razón Social</li>
                    <li><CheckCircle size={16} /> RUT</li>
                    <li><CheckCircle size={16} /> Giro Comercial</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Información de Contacto</h3>
                </div>
                <p className="paso-description">
                  Establece los datos de contacto oficiales de la empresa
                </p>
                <div className="paso-actions">
                  <h4>Datos de contacto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Teléfono</li>
                    <li><CheckCircle size={16} /> Email</li>
                    <li><CheckCircle size={16} /> Sitio Web</li>
                    <li><CheckCircle size={16} /> Dirección física</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <UsersIcon size={24} style={{color: themeColor}} />
                  <h3>Administradores y Equipo</h3>
                </div>
                <p className="paso-description">
                  Visualiza los administradores de la empresa y estadísticas del equipo de trabajo
                </p>
                <div className="paso-actions">
                  <h4>Información mostrada:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Lista de Administradores con email y rol</li>
                    <li><CheckCircle size={16} /> Usuarios Activos (contador en tiempo real)</li>
                    <li><CheckCircle size={16} /> Total de Administradores</li>
                    <li><CheckCircle size={16} /> Enlace a sección Equipo para gestión completa</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'empresas') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Panel Super Administrador</h2>
            <p>
              Esta funcionalidad está disponible SOLO para Super Administradores.
              Gestión completa del sistema PAPER BOAT multiempresa.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Vista del Panel</h3>
                </div>
                <p className="paso-description">
                  El panel muestra estadísticas en tiempo real del sistema completo
                </p>
                <div className="paso-actions">
                  <h4>Métricas mostradas:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Total de Empresas Cliente registradas</li>
                    <li><CheckCircle size={16} /> Usuarios Activos en todo el sistema</li>
                    <li><CheckCircle size={16} /> Pestañas: Empresas, Sistema, Actividad, Super Admins</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow">
                <ArrowRight size={24} />
              </div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Gestión de Empresas Cliente</h3>
                </div>
                <p className="paso-description">
                  Crea y administra las empresas que usan el sistema PAPER BOAT
                </p>
                <div className="paso-actions">
                  <h4>Información por empresa:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre Comercial y RUT</li>
                    <li><CheckCircle size={16} /> Estado (Activa/Inactiva)</li>
                    <li><CheckCircle size={16} /> Cantidad de usuarios totales</li>
                    <li><CheckCircle size={16} /> Cantidad de administradores</li>
                    <li><CheckCircle size={16} /> Acciones: Editar, Ver detalles, Desactivar</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Funcionalidades:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Crear nueva empresa con botón "Nueva Empresa"</li>
                    <li><CheckCircle size={16} /> Ver tabla con TODAS las empresas del sistema</li>
                    <li><CheckCircle size={16} /> Asignar usuarios a cada empresa</li>
                    <li><CheckCircle size={16} /> Estadísticas REALES de usuarios y administradores</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="manual-container">
      <div className="manual-header">
        <div className="header-title">
          <BookOpen size={32} style={{color: '#000000'}} />
          <h1>Manual del Sistema</h1>
        </div>
        <p>Guía completa para el uso del sistema PAPER BOAT</p>
      </div>

      <div className="manual-content">
        <div className="manual-accordion">
          <h3>Secciones</h3>
          <div className="manual-nav">
            {sections.map((section) => (
              <div key={section.id} className="nav-section">
                <button
                  className={`section-toggle ${openSections.includes(section.id) ? 'active' : ''}`}
                  onClick={() => toggleSection(section.id)}
                  style={openSections.includes(section.id) ? {background: themeColor} : {}}
                >
                  <section.icon size={20} />
                  <span>{section.title}</span>
                  <ChevronDown
                    size={16}
                    className={`chevron ${openSections.includes(section.id) ? 'rotated' : ''}`}
                  />
                </button>
                <p className="section-desc">{section.description}</p>

                {/* Contenido expandible */}
                {openSections.includes(section.id) && (
                  <div id={`section-content-${section.id}`} className="section-content-expanded">
                    {renderContent(section.id)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manual;
